<script setup lang="ts">
import { ref } from 'vue'
import { ElInput } from 'element-plus'

/**
 * The text field. Anything `ElInput` takes that is not declared here falls through, so
 * `maxlength`, `rows`, `autocomplete` and the rest still work —
 * including `type="textarea"`.
 *
 * A numeric field is `InputNumber`, which models a number instead of a string. A line or
 * two of domains is `TextareaList`, which splits them for you.
 */
defineProps<{
  /** `null` is accepted so a filter draft can hold an empty value, as in Select. */
  modelValue?: string | number | null
  placeholder?: string
  clearable?: boolean
  disabled?: boolean
  type?: string
  /** Leave unset to inherit the size from ElConfigProvider */
  size?: '' | 'small' | 'default' | 'large'
  /**
   * Centre what is typed. For a field whose value is one short token rather than prose —
   * a digit of a verification code — where left alignment reads as an empty box with
   * something stuck to its edge. Here rather than in the consumer, because reaching the
   * inner element is a `:deep(.el-…)` and those live in this library.
   */
  align?: 'left' | 'center'
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', v: string | number): void
}>()

const inner = ref<InstanceType<typeof ElInput>>()

/**
 * The caret, for a field a parent moves between — a code split across several boxes moves
 * it on every keystroke. `<script setup>` exposes nothing by default, so without this a
 * `ref` on this component reaches an instance that cannot be focused.
 */
defineExpose({
  focus: () => inner.value?.focus(),
  blur: () => inner.value?.blur(),
  select: () => inner.value?.select()
})

/** Fixed affixes rendered outside the field, e.g. a non-editable `https://` prefix. */
defineSlots<{
  prepend?: () => unknown
  append?: () => unknown
}>()
</script>

<template>
  <ElInput
    ref="inner"
    class="shared-input"
    :class="{ 'shared-input--center': align === 'center' }"
    :model-value="modelValue"
    :placeholder="placeholder"
    :clearable="clearable"
    :disabled="disabled"
    :type="type"
    v-bind="$attrs"
    :size="size"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template v-if="$slots.prepend" #prepend>
      <slot name="prepend" />
    </template>
    <template v-if="$slots.append" #append>
      <slot name="append" />
    </template>
  </ElInput>
</template>

<style lang="scss" scoped>
.shared-input--center :deep(.el-input__inner) {
  text-align: center;
}
</style>
